import React, { useRef, useState, useCallback, useEffect } from "react";
import { LogOut, Info, Camera, X, RefreshCw, CheckCircle } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { API_BASE_URL } from "../Api/apiconnector";

const COLORS = ["#3B82F6", "#10B981", "#8B5CF6", "#F59E0B"];

/* ─── Live Camera Modal ─────────────────────────────────────────── */
const CameraModal = ({ onClose, onSuccess }) => {
  const videoRef    = useRef(null);
  const canvasRef   = useRef(null);   // hidden capture canvas
  const detectRef   = useRef(null);   // tiny canvas for skin-pixel sampling
  const rafRef      = useRef(null);   // requestAnimationFrame id
  const streamRef   = useRef(null);
  const holdTimer   = useRef(null);   // countdown before auto-capture
  const colorRef    = useRef("white"); // track current oval color without re-render

  const [phase,         setPhase]         = useState("loading");
  const [capturedImage, setCapturedImage] = useState(null);
  const [errorMsg,      setErrorMsg]      = useState("");
  // oval appearance — updated by detection loop, drives SVG overlay
  const [ovalState, setOvalState] = useState({
    color:   "rgba(255,255,255,0.55)",
    glow:    "none",
    dash:    "8 6",
    label:   "Position your face in the oval",
    progress: 0,   // 0-100, fills arc when face detected
  });

  /* ── skin-tone pixel detector ─────────────────────────────────── */
  // Samples the oval centre AND checks that the outer corners are NOT skin
  // (distinguishes a real face centred in frame from a skin-toned background).
  const getSkinScore = (video) => {
    const dc = detectRef.current;
    if (!dc || !video.videoWidth) return 0;

    const VW = video.videoWidth, VH = video.videoHeight;
    const W = 120, H = 150;
    dc.width = VW; dc.height = VH;
    const ctx = dc.getContext("2d");
    ctx.drawImage(video, 0, 0, VW, VH);

    const isSkin = (r, g, b) => {
      // Rule 1 – RGB skin rules (Kovac et al.)
      const rgbSkin =
        r > 95 && g > 40 && b > 20 &&
        Math.max(r, g, b) - Math.min(r, g, b) > 15 &&
        Math.abs(r - g) > 15 &&
        r > g && r > b;

      // Rule 2 – HSV skin range (stricter)
      const max = Math.max(r, g, b), min = Math.min(r, g, b);
      const v = max / 255;
      const s = max === 0 ? 0 : (max - min) / max;
      let h = 0;
      if (max !== min) {
        if (max === r) h = ((g - b) / (max - min) + 6) % 6;
        else if (max === g) h = (b - r) / (max - min) + 2;
        else h = (r - g) / (max - min) + 4;
        h *= 60;
      }
      const hsvSkin = h >= 0 && h <= 35 && s >= 0.2 && s <= 0.75 && v >= 0.35;

      return rgbSkin && hsvSkin; // BOTH rules must pass
    };

    // Count skin pixels in the CENTRE oval region
    const cx = Math.floor((VW - W) / 2), cy = Math.floor((VH - H) / 2);
    const centreData = ctx.getImageData(cx, cy, W, H).data;
    let centreHits = 0;
    for (let i = 0; i < centreData.length; i += 4)
      if (isSkin(centreData[i], centreData[i+1], centreData[i+2])) centreHits++;
    const centreScore = centreHits / (W * H);

    // Count skin pixels in the FOUR CORNERS (background reference)
    const cornerSize = 40;
    const corners = [
      ctx.getImageData(0, 0, cornerSize, cornerSize).data,
      ctx.getImageData(VW - cornerSize, 0, cornerSize, cornerSize).data,
      ctx.getImageData(0, VH - cornerSize, cornerSize, cornerSize).data,
      ctx.getImageData(VW - cornerSize, VH - cornerSize, cornerSize, cornerSize).data,
    ];
    let cornerHits = 0, cornerTotal = 0;
    corners.forEach(d => {
      for (let i = 0; i < d.length; i += 4) {
        if (isSkin(d[i], d[i+1], d[i+2])) cornerHits++;
        cornerTotal++;
      }
    });
    const bgSkinRatio = cornerHits / cornerTotal;

    // Final score = centre skin MINUS background skin (removes false positives)
    return Math.max(0, centreScore - bgSkinRatio * 1.5);
  };

  /* ── detection loop ───────────────────────────────────────────── */
  const progressRef   = useRef(0);
  const noFaceFrames  = useRef(0); // consecutive frames with no face

  const runDetection = useCallback(() => {
    const video = videoRef.current;
    if (!video || video.paused || video.ended) return;

    const score    = getSkinScore(video);
    const detected = score > 0.20; // stricter: need 20 % net skin after bg subtraction

    if (detected) {
      noFaceFrames.current = 0;
      progressRef.current  = Math.min(100, progressRef.current + 2.5);
    } else {
      noFaceFrames.current += 1;
      // Drop fast after 3 consecutive no-face frames (≈50 ms at 60 fps)
      if (noFaceFrames.current >= 3) {
        progressRef.current = Math.max(0, progressRef.current - 8); // fast drop
        if (holdTimer.current) { clearTimeout(holdTimer.current); holdTimer.current = null; }
      }
    }

    const p = progressRef.current;
    let color, glow, dash, label;

    if (p >= 100) {
      color = "#22c55e";
      glow  = "0 0 16px 4px rgba(34,197,94,0.55)";
      dash  = "0";
      label = "✓ Face detected — hold still";
    } else if (p > 45) {
      color = "#eab308";
      glow  = "0 0 10px 2px rgba(234,179,8,0.4)";
      dash  = "6 5";
      label = "Getting there… move closer";
    } else {
      color = "rgba(255,255,255,0.55)";
      glow  = "none";
      dash  = "8 6";
      label = p > 0 ? "Keep your face centred" : "Position your face in the oval";
    }

    if (colorRef.current !== color) {
      colorRef.current = color;
      setOvalState({ color, glow, dash, label, progress: p });
    } else {
      setOvalState(prev => ({ ...prev, progress: p, label }));
    }

    rafRef.current = requestAnimationFrame(runDetection);
  }, []);

  /* ── camera ───────────────────────────────────────────────────── */
  const stopCamera = () => {
    cancelAnimationFrame(rafRef.current);
    clearTimeout(holdTimer.current);
    holdTimer.current = null;
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    progressRef.current  = 0;
    noFaceFrames.current = 0;
  };

  const startCamera = useCallback(async () => {
    setPhase("loading");
    setErrorMsg("");
    setOvalState({ color: "rgba(255,255,255,0.55)", glow: "none", dash: "8 6", label: "Position your face in the oval", progress: 0 });
    progressRef.current  = 0;
    noFaceFrames.current = 0;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setPhase("live");
      rafRef.current = requestAnimationFrame(runDetection);
    } catch {
      setErrorMsg("Camera access denied or unavailable.");
      setPhase("error");
    }
  }, [runDetection]);

  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, [startCamera]);

  /* ── capture ──────────────────────────────────────────────────── */
  const handleCapture = () => {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width  = video.videoWidth  || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext("2d");
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    setCapturedImage(canvas.toDataURL("image/jpeg", 0.92));
    stopCamera();
    setPhase("preview");
  };

  const handleRetake = () => { setCapturedImage(null); startCamera(); };

  /* ── upload ───────────────────────────────────────────────────── */

  const handleSubmit = async () => {
    setPhase("uploading");

    const token = localStorage.getItem("token");

    const userData = JSON.parse(localStorage.getItem("user"));
    const userId = userData?.user?.user_id;

    const payload = {
      user_id: userId,
      image: capturedImage
    };

    try {
      const res = await fetch(`${API_BASE_URL}/register-client-face/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (!res.ok || result.error) {
        throw new Error(result.error || `Server error ${res.status}`);
      }

      setPhase("done");
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 1800);

    } catch (err) {
      setErrorMsg(err.message || "Registration failed.");
      setPhase("error");
    }
  };
  const handleClose = () => { stopCamera(); onClose(); };

  /* ── SVG arc progress helper ──────────────────────────────────── */
  // Draws a coloured arc around the oval (cx, cy = centre; rx, ry = radii)
  const ArcProgress = ({ progress, color }) => {
    const cx = 50, cy = 50, rx = 34, ry = 42;
    const pct = Math.min(progress / 100, 1);
    if (pct <= 0) return null;
    // Parametric ellipse point
    const angle = (pct * 360 - 90) * (Math.PI / 180);
    const startX = cx + rx * Math.cos(-Math.PI / 2);
    const startY = cy + ry * Math.sin(-Math.PI / 2);
    const endX   = cx + rx * Math.cos(angle);
    const endY   = cy + ry * Math.sin(angle);
    const large  = pct > 0.5 ? 1 : 0;
    const d = pct >= 0.999
      ? `M ${cx} ${cy - ry} A ${rx} ${ry} 0 1 1 ${cx - 0.001} ${cy - ry} Z`
      : `M ${startX} ${startY} A ${rx} ${ry} 0 ${large} 1 ${endX} ${endY}`;
    return (
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth="3"
        strokeLinecap="round"
        style={{ filter: `drop-shadow(0 0 4px ${color})`, transition: "stroke 0.4s" }}
      />
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(5px)" }}
    >
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <Camera size={18} className="text-blue-500" />
            <span className="font-semibold text-gray-900 dark:text-gray-100">Register Face</span>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors rounded-lg p-1">
            <X size={20} />
          </button>
        </div>

        {/* Camera / Preview area */}
        <div className="relative bg-black" style={{ aspectRatio: "4/3" }}>

          {/* Live video */}
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            style={{ display: phase === "live" ? "block" : "none", transform: "scaleX(-1)" }}
            playsInline
            muted
          />

          {/* Captured preview */}
          {phase === "preview" && capturedImage && (
            <img src={capturedImage} alt="Captured" className="w-full h-full object-cover" />
          )}

          {/* Loading */}
          {phase === "loading" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white">
              <div className="w-9 h-9 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span className="text-sm opacity-70">Starting camera…</span>
            </div>
          )}

          {/* Uploading */}
          {phase === "uploading" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/60 text-white">
              <div className="w-9 h-9 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
              <span className="text-sm">Registering face…</span>
            </div>
          )}

          {/* Success */}
          {phase === "done" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/70 text-white">
              <CheckCircle size={52} className="text-green-400" />
              <span className="font-semibold">Face Registered!</span>
            </div>
          )}

          {/* Error */}
          {phase === "error" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/85 text-white px-8 text-center">
              <span className="text-red-400 text-sm leading-relaxed">{errorMsg}</span>
              <button
                onClick={startCamera}
                className="flex items-center gap-2 text-sm bg-white/10 hover:bg-white/20 px-4 py-2 rounded-xl transition-colors"
              >
                <RefreshCw size={14} /> Retry
              </button>
            </div>
          )}

          {/* ── Smart oval overlay (live only) ── */}
          {phase === "live" && (
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              {/* Dark backdrop with oval cut-out */}
              <svg
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                className="absolute inset-0 w-full h-full"
                style={{ mixBlendMode: "normal" }}
              >
                <defs>
                  <mask id="ovalMask">
                    <rect width="100" height="100" fill="white" />
                    <ellipse cx="50" cy="50" rx="34" ry="42" fill="black" />
                  </mask>
                </defs>
                {/* Dark overlay outside oval */}
                <rect width="100" height="100" fill="rgba(0,0,0,0.38)" mask="url(#ovalMask)" />
                {/* Oval border — changes colour */}
                <ellipse
                  cx="50" cy="50" rx="34" ry="42"
                  fill="none"
                  stroke={ovalState.color}
                  strokeWidth="1.8"
                  strokeDasharray={ovalState.dash}
                  style={{
                    transition: "stroke 0.4s ease, filter 0.4s ease",
                    filter: ovalState.glow !== "none"
                      ? `drop-shadow(0 0 6px ${ovalState.color})`
                      : "none",
                  }}
                />
                {/* Progress arc on top */}
                <ArcProgress progress={ovalState.progress} color={ovalState.color} />
              </svg>

              {/* Status label below oval */}
              <div
                className="absolute bottom-3 left-0 right-0 flex justify-center"
              >
                <span
                  className="text-xs font-medium px-3 py-1 rounded-full"
                  style={{
                    background: "rgba(0,0,0,0.55)",
                    color: ovalState.progress >= 100 ? "#4ade80"
                          : ovalState.progress > 40  ? "#facc15"
                          : "rgba(255,255,255,0.85)",
                    transition: "color 0.4s",
                  }}
                >
                  {ovalState.label}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Hidden canvases */}
        <canvas ref={canvasRef}  className="hidden" />
        <canvas ref={detectRef}  className="hidden" />

        {/* Footer */}
        <div className="px-5 py-4 space-y-3">
          {/* Hint */}
          {phase === "live" && (
            <p className="text-center text-xs text-gray-400">
              Align your face within the oval, then tap <strong>Capture</strong>
            </p>
          )}
          {phase === "preview" && (
            <p className="text-center text-xs text-gray-400">
              Looks good? Hit <strong>Submit</strong>, or <strong>Retake</strong> if needed
            </p>
          )}

          {/* Action buttons */}
          <div className="flex items-center justify-center gap-3">
            {phase === "live" && (
              <button
                onClick={handleCapture}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white font-semibold px-7 py-2.5 rounded-xl transition-all"
              >
                <Camera size={17} /> Capture
              </button>
            )}

            {phase === "preview" && (
              <>
                <button
                  onClick={handleRetake}
                  className="flex items-center gap-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 font-semibold px-5 py-2.5 rounded-xl transition-colors"
                >
                  <RefreshCw size={15} /> Retake
                </button>
                <button
                  onClick={handleSubmit}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700 active:scale-95 text-white font-semibold px-6 py-2.5 rounded-xl transition-all"
                >
                  <CheckCircle size={17} /> Submit
                </button>
              </>
            )}

            {(phase === "loading" || phase === "uploading" || phase === "done") && (
              <button disabled className="opacity-40 cursor-not-allowed bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold px-6 py-2.5 rounded-xl">
                {phase === "done" ? "Done ✓" : "Please wait…"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Main Sidebar ──────────────────────────────────────────────── */
const ClientSidebar = ({ summary = {} }) => {
  const ct = Number(summary.ct) || 0;
  const mri = Number(summary.mri) || 0;
  const xray = Number(summary.xray) || 0;
  const totalSent = Number(summary.totalSent) || 0;
  const totalReported = Number(summary.totalReported) || 0;
  const totalPending = Number(summary.totalPending) || 0;

  const modalitySum = ct + mri + xray;
  const other = Math.max(0, totalSent - modalitySum);

  const data = [
    { name: "CT", value: ct, color: COLORS[0] },
    { name: "MRI", value: mri, color: COLORS[1] },
    { name: "X-Ray", value: xray, color: COLORS[2] },
  ];
  if (other > 0) data.push({ name: "Other", value: other, color: COLORS[3] });

  const totalForChart = data.reduce((s, d) => s + d.value, 0);
  const completionRate = totalSent ? Math.round((totalReported / totalSent) * 100) : 0;

  const [showCamera, setShowCamera] = useState(false);
  const [faceRegistered, setFaceRegistered] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem("token");
    sessionStorage.clear();
    window.location.href = "/";
  };

  return (
    <>
      {showCamera && (
        <CameraModal
          onClose={() => setShowCamera(false)}
          onSuccess={() => setFaceRegistered(true)}
        />
      )}

      <div className="w-80 h-screen overflow-y-auto bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-xl rounded-2xl p-5 flex flex-col justify-between">
        {/* Header */}
        <div>
          <h3 className="font-bold text-2xl mb-4 flex items-center gap-2">
            <span role="img" aria-label="chart">📊</span> Dashboard Summary
          </h3>

          {/* Donut chart */}
          <div className="w-full h-44 mb-2">
            {totalForChart === 0 ? (
              <div className="flex items-center justify-center h-full text-sm text-gray-500">No modality data</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data} dataKey="value" innerRadius={44} outerRadius={70} startAngle={90} endAngle={-270} paddingAngle={4} labelLine={false} label={false}>
                    {data.map((entry, idx) => <Cell key={`cell-${idx}`} fill={entry.color} />)}
                  </Pie>
                  <Tooltip formatter={(value, name) => {
                    const pct = totalForChart ? Math.round((value / totalForChart) * 100) : 0;
                    return [`${value} (${pct}%)`, name];
                  }} />
                </PieChart>
              </ResponsiveContainer>
            )}
            <div className="mt-2 flex items-center justify-center">
              <div className="text-center">
                <div className="text-xs text-gray-500">Total Scans</div>
                <div className="text-xl font-bold">{totalSent}</div>
                <div className="text-xs text-gray-500">{completionRate}% reported</div>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="mt-3 space-y-2">
            {data.map((d) => {
              const pct = totalForChart ? Math.round((d.value / totalForChart) * 100) : 0;
              return (
                <div key={d.name} className="flex justify-between items-center text-sm">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full inline-block" style={{ background: d.color }} />
                    <span>{d.name}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-semibold">{d.value}</span>
                    <span className="ml-2 text-xs text-gray-400">({pct}%)</span>
                  </div>
                </div>
              );
            })}
          </div>

          <hr className="my-3 border-gray-200 dark:border-gray-700" />

          {/* Quick stats */}
          <div className="text-sm space-y-2">
            <div className="flex justify-between">
              <span>Total Sent:</span>
              <span className="font-semibold">{totalSent}</span>
            </div>
            <div className="flex justify-between">
              <span>Total Reported:</span>
              <span className="font-semibold text-green-600">{totalReported}</span>
            </div>
            <div className="flex justify-between">
              <span>Total Pending:</span>
              <span className="font-semibold text-yellow-600">{totalPending}</span>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-5">
            <div className="text-xs text-gray-600 mb-1">Completion Rate</div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div className="bg-green-500 h-2 rounded-full transition-all" style={{ width: `${completionRate}%` }} />
            </div>
            <div className="text-xs mt-1 text-gray-500">{completionRate}%</div>
          </div>

          {/* Tip box */}
          <div className="mt-5 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-100 dark:border-blue-800 text-xs text-gray-700 dark:text-gray-300 flex gap-2">
            <Info size={16} className="text-blue-500 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-600 dark:text-blue-300 mb-1">Quick Tip</p>
              <ul className="list-disc ml-4 space-y-1">
                <li>Once a new case arrives, upload the patient's history and write the patient note within 10 minutes.</li>
                <li>Check patient details, and if anything is wrong, immediately inform the coordinator.</li>
                <li>After reporting, double-check the report for any errors before final submission.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom actions */}
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-3">

          <button
            onClick={() => setShowCamera(true)}
            className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white py-2 rounded-lg font-semibold transition-all"
          >
            <Camera size={18} />
            {faceRegistered ? "Re-register Face" : "Register Face"}
          </button>

          {faceRegistered && (
            <p className="text-xs text-center text-green-600 dark:text-green-400">
              ✓ Face registered successfully
            </p>
          )}

          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg font-semibold transition-colors"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </div>
    </>
  );
};

export default ClientSidebar;